import { ReadinessRing } from "../../components/ReadinessRing";
import { ParentSurface } from "./ParentSurface";

interface ParentProgressScreenProps {
  childName: string;
  daysPractisedOfLastSeven: number;
  streakDays: number;
  lastMock?: string | undefined;
  subjects: { subject: string; readiness: number; note: string }[];
}

/**
 * Screen 22c — read-only, and deliberately so.
 *
 * A parent can see progress and nothing else: no answers, no session detail, no
 * controls. The child's practice is theirs. The footer routes support back to
 * SMS because the parent has no account to log into.
 */
export function ParentProgressScreen({
  childName,
  daysPractisedOfLastSeven,
  streakDays,
  lastMock,
  subjects,
}: ParentProgressScreenProps) {
  return (
    <ParentSurface>
      <div className="flex items-baseline justify-between pt-6">
        <h1 className="m-0 font-[family-name:var(--font-display)] text-[20px] font-bold">
          How {childName} is doing
        </h1>
        <span className="text-[11.5px] text-text-muted">Updated today</span>
      </div>

      <p className="tabular m-0 pt-3 text-[13.5px] leading-[1.6] text-text-muted">
        Practiced{" "}
        <span className="font-semibold text-text">
          {daysPractisedOfLastSeven} of the last 7 days
        </span>{" "}
        — a {streakDays}-day streak going.
        {lastMock ? (
          <>
            {" "}
            Last mock: <span className="font-semibold text-text">{lastMock}</span>
          </>
        ) : null}
      </p>

      <ul className="m-0 flex list-none flex-col gap-2.5 p-0 pt-6">
        {subjects.map((entry) => (
          <li
            key={entry.subject}
            className="flex items-center gap-3.5 rounded-[var(--radius-card)] border border-border bg-surface p-3.5"
          >
            <ReadinessRing percentage={entry.readiness} size={60} />
            <div className="flex flex-col gap-0.5">
              <span className="text-[14px] font-semibold">{entry.subject}</span>
              <span className="text-[12px] text-text-muted">{entry.note}</span>
            </div>
          </li>
        ))}
      </ul>

      <p className="m-0 pt-6 text-[13px] leading-[1.6] text-text-muted">
        The best help right now: a quiet 20 minutes a day. {childName}'s streak grows every day they
        practice.
      </p>

      <p className="m-0 mt-auto pt-8 text-center text-[12.5px] text-text-muted">
        Questions? Just reply to our SMS.
      </p>
    </ParentSurface>
  );
}
